import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  NgZone,
  OnDestroy,
  OnInit, Output,
  ViewChild, ViewEncapsulation
} from '@angular/core';

import * as go from 'src/libs/gojs/release/go';
import {DiagramEvent, GraphLinksModel, List, Point} from "src/libs/gojs/release/go";
import {DiagramComponent} from "../../gojs/components/diagram/diagram.component";

import {LegacyNavigateService} from "../../core/services/legacy-navigate/legacy-navigate.service";
import {ResizeObserver} from "@juggle/resize-observer";
import {UtilService} from "../../core/services/util/util.service";

import {ActivatedRoute, Router} from "@angular/router";
import {Subscription} from "rxjs";
import {RequestService} from "../../core/services/request/request.service";
import {WorkflowDetailsDataModel} from "./workflow-details-data.model";
import {WorkflowTransitionDataModel} from "./workflow-transition-data.model";
import {WorkflowActivityDataModel} from "./workflow-activity-data.model";
import {WorkflowActivityDefinitionModel} from './workflow-activity-definition.model';

import {OrthogonalLinkReshapingTool} from "../../gojs/extensions/OrthogonalLinkReshapingTool";

import {cloneDeep} from 'lodash';
import {UntypedFormBuilder, UntypedFormGroup} from "@angular/forms";
import {CustomLink} from "./workflow-custom-link";
import {SnapLinkReshapingTool} from "../../gojs/extensions/SnapLinkReshapingTool";
import {OrthogonalSnapLinkReshapingTool} from "../../gojs/extensions/OrthogonalSnapLinkReshapingTool";
import {WorkflowHistoryDataModel} from './workflow-history-data.model';
import {InternationalizationService} from "../../core/services/internationalization/internationalization.service";
import {AlertService} from "../../core/services/alert/alert.service";

// Shortcut for GoJS make method, in the examples this is $, but that conflicts with jQuery
const $$ = go.GraphObject.make;

// Should move these to globals.d.ts
declare let $: any;
declare let Navigate: any;

@Component({
  selector: 'sb-workflow-page',
  templateUrl: './workflow-page.component.html',
  styleUrls: ['./workflow-page.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class WorkflowPageComponent implements OnInit, AfterViewInit, OnDestroy {
  // Reference data

  private BADGE_FONT = 'normal 10pt "Source Sans Pro", sans-serif';
  private NORMAL_FONT = 'normal 10pt "Source Sans Pro", sans-serif';
  private NORMAL_FONT_LARGE = 'normal 11pt "Source Sans Pro", sans-serif';
  private SMALL_NORMAL_FONT = 'normal 8pt "Source Sans Pro", sans-serif';
  private ITALIC_FONT = 'italic 10pt "Source Sans Pro", sans-serif';
  private BOLD_FONT = 'bold 10pt "Source Sans Pro", sans-serif';
  private BOLD_FONT_LARGE = 'bold 11pt "Source Sans Pro", sans-serif';
  private FA_EQUIVALENT_FONT = 'normal 24px "Source Sans Pro", sans-serif';
  private MI_ICON_FONT = '14px Material Symbols Outlined';
  private MARGIN_5252 = new go.Margin(5, 2, 5, 2);
  private MARGIN_525_30 = new go.Margin(5, 2, 5, 30);
  private MARGIN_523_60 = new go.Margin(5, 2, 3, 60);

  private LINK_INACTIVE_COLOUR = "#EFEFEF";
  private LINK_ACTIVE_COLOUR = "#AAA";

  private BASE_ACTIVITY_WIDTH = 200;

  private ACTIVITY_DEFINITION_MAP: Map<string, WorkflowActivityDefinitionModel> = new Map<string, WorkflowActivityDefinitionModel>();
  private ACTIVITY_DEFINITION_ARRAY: WorkflowActivityDefinitionModel[] = [];

  private COLOUR_MAP = {};

  private HISTORY_CURRENT_STATUS_COLOUR = '#51C17C';
  private HISTORY_ACTIVITY_VISITED_COLOUR = '#3E8AD6';

  // Exposed diagram data

  public diagramNodeData: go.ObjectData[] = [];
  public diagramLinkData: go.ObjectData[] = [];
  public diagramModelData: go.ObjectData = { prop: 'value' };
  public skipsDiagramUpdate = false;

  // Exposed palette data

  public paletteNodeData: go.ObjectData[] = [];
  public paletteLinkData: go.ObjectData[] = [];
  public paletteModelData: go.ObjectData = { prop: 'value' };

  // Exposed transitions palette data
  public transitionsPaletteNodeData: go.ObjectData[] = [];
  public transitionsPaletteLinkData: go.ObjectData[] = [];
  public transitionsPaletteModelData: go.ObjectData = { prop: 'value' };

  // Exposed state
  public title: string = "";

  public isLoaded: boolean = false;
  public isEdit: boolean = true;

  public isCheckedOut: boolean = false;
  public isCheckedOutByThisUser: boolean = false;

  public checkedOutBy: string;

  // Exposed overview data

  public observedDiagram = null;

  // Exposed tools state

  workflowForm: UntypedFormGroup;

  // Output that emits the title - for use by the workflow-modal
  @Output()
  title$ = new EventEmitter<string>();

  @Output()
  workflowInstanceID$ = new EventEmitter<string>();

  // Internal state

  private activatedRouteSubscription: Subscription | null;
  private workflowId: string;
  private workflowTable: string;
  private workflowData: WorkflowDetailsDataModel;
  private componentFirstDisplay = true;

  private workflowFormInitialState: any;

  private isDestroyed = false;

  // GoJS

  private gojsDiagram: go.Diagram;

  @ViewChild('gojsDiagram', { static: false }) public gojsDiagramComponent: DiagramComponent;

  private gojsPalette: go.Diagram;

  @ViewChild('gojsPalette', { static: false }) public gojsPaletteComponent: DiagramComponent;

  private gojsControls: go.Diagram;

  @ViewChild('gojsControls', { static: false }) public gojsControlsComponent: DiagramComponent;

  private gojsTransitionsPalette: go.Diagram;

  @ViewChild('gojsTransitionsPalette', { static: false }) public gojsTransitionsPaletteComponent: DiagramComponent;

  // Resize support

  @ViewChild("diagramColumn", {static: false})
  private diagramColumn: ElementRef;

  @ViewChild("paletteColumn", {static: false})
  private paletteColumn: ElementRef;

  @ViewChild("toolsColumn", {static: false})
  private toolsColumn: ElementRef;

  private resizeObserver: ResizeObserver;

  // Form support

  private formStatusSubscription: Subscription;
  private formValueSubscription: Subscription;

  constructor(private cdr: ChangeDetectorRef,
              private activatedRoute: ActivatedRoute,
              private legacyNavigateService: LegacyNavigateService,
              private i18nService: InternationalizationService,
              private utilService: UtilService,
              private requestService: RequestService,
              private alertService: AlertService,
              private formBuilder: UntypedFormBuilder,
              private zone: NgZone,
              private router: Router) {}

  ngOnInit(): void {

    // Resize support
    this.resizeObserver = new ResizeObserver((entries, observer) => {
      this.zone.run(() => {
        entries.forEach(this.getResizeComponentFunction());
      });
    });

    this.resizeObserver.observe(document.body);

    // Identify the displayType from the route.
    this.activatedRouteSubscription = this.activatedRoute.paramMap.subscribe(async params => {
      let id = params.get("id"); // 'id' of a workflow itself, or a record that is running a workflow.
      let table = params.get("table"); // name of the Workflow table itself, or the name of a table for which a record id is being passed in.
      this.workflowId = id;
      this.workflowTable = table;

      // If we don't get id or table it means we have been popped up in a modal. Get the data from window.location
      if (!this.workflowId && !this.workflowTable) {
        const parts = window.location.hash.substring(2).split("/");
        this.workflowId = parts[1];
        this.workflowTable = parts[0];
      }

      // Only show the palette and tools if we are editing a workflow, not displaying a workflow instance.
      this.isEdit = this.workflowTable === "Workflow";

      // We only do this if we are not in a modal
      if (this.isEdit) {
        // Wipe out the page context
        (window as any).KO_PAGE_CONTEXT.clear();

        // Wipe out the Knockout content.
        const pageContentMain = document.getElementById("page-content-main");
        if (pageContentMain) {
          pageContentMain.innerHTML = "";
        }
        const errorConsole = document.getElementById("error-console");
        if (errorConsole) {
          errorConsole.innerHTML = "";
        }

        // Mark as portal page
        document.getElementById("detailMain")?.classList?.add("sb-workflow-page-content");

        this.router.events.subscribe(
          event => {
            this.ngOnDestroy();
          });
      }

      // We may be changing routes on the same Workflow component so we need to clear it all first.
      if (!this.componentFirstDisplay) {
        this.clearDiagram();
        this.displayDiagram();

        this.clearTransitionsPalette();
        this.displayTransitionsPalette();
      }
    });

    // Form support - initial values

    this.workflowForm = this.formBuilder.group({
    });

    this.workflowFormInitialState = this.workflowForm.value;

    this.formStatusSubscription = this.workflowForm.statusChanges.subscribe((status) => {
    });

    this.formValueSubscription = this.workflowForm.valueChanges.subscribe((value) => {
        this.initLayout();
    });
  }

  private getResizeComponentFunction() {
    return (resizeObserverEntry) => {
      if (resizeObserverEntry.target === this.diagramColumn.nativeElement) {
        if (this.gojsDiagram) {
          const requiredWidth = resizeObserverEntry.contentRect.width + 'px';
          this.gojsDiagram.div.style.width = requiredWidth;
          this.gojsDiagram.requestUpdate();

        }
      } else if (resizeObserverEntry.target === document.body) {
        this.resizeDiagramControlsVertical();
      }
    };
  }

  private resizeDiagramControlsVertical() {
    let parentContainer = $('#detailMain');

    if (parentContainer.length === 0) {
      parentContainer = $('.servicely-content-landingpage');
    }

    const jQueryInnerHeight = parentContainer.innerHeight();
    const contentRecHeight = jQueryInnerHeight - 120;

    if (this.gojsDiagram && this.diagramColumn) {
      this.diagramColumn.nativeElement.style.height = contentRecHeight + 'px';
      this.gojsDiagram.div.style.height = contentRecHeight + 'px';
      this.gojsDiagram.requestUpdate();
    }

    if (this.gojsPalette && this.paletteColumn) {
      this.paletteColumn.nativeElement.style.height = contentRecHeight + 'px';
      this.gojsPalette.div.style.height = (contentRecHeight - 25) + 'px';
      this.gojsPalette.requestUpdate();
    }

    if (this.toolsColumn) {
      this.toolsColumn.nativeElement.style.height = contentRecHeight + 'px';
    }
  }

  async ngAfterViewInit() {
    await this.loadWorkflowConfiguration();
    this.isLoaded = true;

    this.resizeObserver.observe(this.diagramColumn.nativeElement);
    if (this.isEdit) {
      this.resizeObserver.observe(this.toolsColumn.nativeElement);
      this.resizeObserver.observe(this.paletteColumn.nativeElement);
    }

    await this.displayDiagram();
    if (this.isEdit) {
      await this.displayTransitionsPalette();
      await this.displayPalette();
    }
    this.componentFirstDisplay = false;

    setTimeout(() => {
      this.zone.run(() => {
        this.resizeDiagramControlsVertical();
      });
    });
  }

  ngOnDestroy(): void {
    if (this.isDestroyed) {
      return;
    } else {
      this.isDestroyed = true;
    }

    document.getElementById("detailMain")?.classList?.remove("sb-workflow-page-content");

    this.resizeObserver.unobserve(this.diagramColumn.nativeElement);

    if (this.isEdit) {
      this.resizeObserver.unobserve(this.toolsColumn.nativeElement);
      this.resizeObserver.unobserve(this.paletteColumn.nativeElement);
    }

    this.resizeObserver.unobserve(document.body);
    this.activatedRouteSubscription.unsubscribe();

    this.formStatusSubscription && this.formStatusSubscription.unsubscribe();
    this.formValueSubscription && this.formValueSubscription.unsubscribe();
  }

  /**
   * Load the Workflow Diagram/Activity Configuration from the DB
   */
  async loadWorkflowConfiguration() {
    const response: any = await
      this.requestService
        .createRequest()
        .controller("servicely.core.Workflow")
        .method("getWorkflowActivityConfiguration")
        .data({})
        .execute();

    const data = response.data as WorkflowActivityDefinitionModel[];

    data.forEach(it => {
      it.Title = this.i18nService.getMessage(it.Title);
      this.ACTIVITY_DEFINITION_MAP[it.Name] = it;
      this.ACTIVITY_DEFINITION_ARRAY.push(it);
    });

    return response.data;
  }

  // Methods used in the template

  public getInitDiagram(): () => go.Diagram {
    return () => { return this.initDiagram(); };
  }

  public getInitPalette(): () => go.Diagram {
    return () => { return this.initPalette(); };
  }

  public getInitOverview(): () => go.Overview {
    return () => { return this.initOverview(); };
  }

  public getInitControls(): () => go.Diagram {
    return () => { return this.initControls(); };
  }

  public getInitTransitionsPalette(): () => go.Diagram {
    return () => { return this.initTransitionsPalette(); };
  }

  // Common methods

  private async displayDiagram() {
    await this.initData();
    this.linkOverviewToDiagram();
    this.initControlButtons();
    this.initDecorations();
    this.utilService.tickThen(() => {this.populateDiagram()});
  }

  private async displayPalette() {
    this.utilService.tickThen(() => {this.populatePalette()});
  }

  private updateDiagramFromModel() {
    this.gojsDiagram.updateAllRelationshipsFromData();
    this.gojsDiagram.updateAllTargetBindings();

    this.clearTransitionsPalette();
    this.displayTransitionsPalette();
  }

  private getModel(): GraphLinksModel {
    return this.gojsDiagram.model as GraphLinksModel;
  }

  private getPaletteModel(): GraphLinksModel {
    return this.gojsPalette.model as GraphLinksModel;
  }

  private getTransitionsPaletteModel(): GraphLinksModel {
    return this.gojsTransitionsPalette?.model as GraphLinksModel;
  }

  private setKeyForNodeData(nodeData: any, key: string) {
    this.getModel().setKeyForNodeData(nodeData, key);
  }

  private setKeyForLinkData(linkData: any, key: string) {
    this.getModel().setKeyForLinkData(linkData, key);
  }

  private initDiagram(): go.Diagram {

    // const linkReshapingTool =  new OrthogonalLinkReshapingTool();
    // const linkReshapingTool =  new SnapLinkReshapingTool();
    const linkReshapingTool =  new OrthogonalSnapLinkReshapingTool();
    linkReshapingTool.avoidsNodes = false;

    const dia = $$(go.Diagram, {
      isReadOnly: !this.isEdit,
      "LayoutCompleted": (e) => {
      },
      "linkReshapingTool": linkReshapingTool
    });

    // Needs a model to start with or the Angular component complains
    dia.model = $$(go.GraphLinksModel,
      {
        linkToPortIdProperty: 'toPort',
        linkFromPortIdProperty: 'fromPort',
        linkKeyProperty: 'key',
        skipsUndoManager: true,
        copiesKey: false,
      }
    );

    this.gojsDiagram = dia;

    dia.addDiagramListener("LinkDrawn", async (e: DiagramEvent) => {
      // This listener looks at links drawn on the diagram.
      // The key will be generated by goJS so we need to replace it with the correct key.
      const drawnLink: go.Link = e.subject;
      const linkModel = this.getLinkModelById(drawnLink.key as string);

      const fromPort = linkModel.fromPort;
      const toPort = linkModel.toPort;
      const fromId = linkModel.from;
      const toId = linkModel.to;

      const existingTransition = this.workflowData.Workflow.Transitions.find((a) => a.Code === fromPort && a.FromActivity === fromId);
      if (existingTransition) {
        this.setKeyForLinkData(linkModel, existingTransition.id);
        await this.setTransitionToActivity(existingTransition.id, toId);
        await this.synchronizeWorkflowData();
        this.updateDiagramFromModel();
      } else {
        const newId = await this.createFixedTransition(linkModel);
        if (newId) {
          this.setKeyForLinkData(linkModel, newId);
          await this.synchronizeWorkflowData();
          this.updateDiagramFromModel();
        } else {
          this.getModel().removeLinkData(linkModel);
        }
      }
    });

    dia.addDiagramListener("LinkReshaped", async (e: DiagramEvent) => {
      // This listener looks at links reshaped on the diagram.
      const drawnLink: go.Link = e.subject;

      const linkModel = this.getLinkModelById(drawnLink.key as string);

      await this.savePoints(linkModel);
    });

    dia.addDiagramListener("ExternalObjectsDropped", async (e: DiagramEvent) => {
      // This listener looks at the items dropped from the palette. We create an activity for each one.
      // The droppedItem and underlying nodeData have already been copied and placed in the diagram by goJS.
      const droppedItem: go.Node = e.subject.first();
      const nodeModel = this.getNodeModelById(droppedItem.key as string);

      if (nodeModel.category === "WorkflowTransition") {
        // This is a drop from the 'Other transitions' palette onto the diagram. We reject it.
        this.getModel().removeNodeData(nodeModel);
      } else {
        const newId = await this.createActivity(nodeModel);
        if (newId) {
          this.setKeyForNodeData(nodeModel, newId);
          await this.synchronizeWorkflowData();
          this.updateDiagramFromModel();
        } else {
          this.getModel().removeNodeData(nodeModel);
        }
      }
    });

    dia.addDiagramListener("SelectionMoved", (e: DiagramEvent) => {

      const parts: go.Set<go.Node> = e.subject;

      parts.each((part) => {
        part.findLinksConnected()
        const data = part.data;
        this.saveLocation(data);

        const affectedLinks = part.findLinksConnected();
        affectedLinks.each((link) => {
          const linkModel = this.getLinkModelById(link.key as string);
          this.savePoints(linkModel);
        });
      });

    });

    dia.addDiagramListener("SelectionDeleted", (e: DiagramEvent) => {
      const parts: go.Set<go.Part> = e.subject;

      parts.each((part) => {
        if (part instanceof go.Link) {
          const linkModel = part.data;
          this.setTransitionToActivity(linkModel.id, null);
        }
      });
    });

    return dia;
  }

  private initPalette(): go.Diagram {

    const dia = $$(go.Palette,{
      scale: 1.0,
      allowHorizontalScroll: false,
      "panningTool.isEnabled": false,
      'dragSelectingTool.isEnabled': false,
      autoScrollRegion: 0
    });

    dia.model = $$(go.GraphLinksModel,
      {
        linkToPortIdProperty: 'toPort',
        linkFromPortIdProperty: 'fromPort',
        linkKeyProperty: 'key'
      }
    );

    dia.layout = $$(go.GridLayout, {
      wrappingColumn: 1
    });

    this.gojsPalette = dia;

    this.definePaletteTemplates();

    return dia;
  }

  private initTransitionsPalette(): go.Diagram {

    const dia = $$(go.Palette,{
      scale: 0.8,
      allowHorizontalScroll: false,
      "panningTool.isEnabled": false,
      'dragSelectingTool.isEnabled': false,
      autoScrollRegion: 0
    });

    dia.model = $$(go.GraphLinksModel,
      {
        linkToPortIdProperty: 'toPort',
        linkFromPortIdProperty: 'fromPort',
        linkKeyProperty: 'key'
      }
    );

    dia.layout = $$(go.GridLayout, {
      wrappingColumn: 1
    });

    this.gojsTransitionsPalette = dia;

    this.defineTransitionPaletteTemplates();

    return dia;
  }

  private clearDiagram() {
    this.removeAllNodes();
    this.removeAllLinks();
  }

  private clearTransitionsPalette() {
    const model = this.getTransitionsPaletteModel();
    if (model && model.nodeDataArray) {
      model.nodeDataArray
        .map((n) => n.key)
        .forEach((k) => {
          model.removeNodeData(model.findNodeDataForKey(k));
        });
    }
  }

  private displayTransitionsPalette() {
    const model = this.getTransitionsPaletteModel();

    if (model) {
      const newNodes = this.workflowData.Workflow.Transitions.flatMap((link) => {

        const node = this.createNodeDataFromWorkflowTransitionDataModelForTransitionPalette(link, this.workflowData);

        if (!node.fromId) {
          return [node];
        } else {
          const fromNode = this.workflowData.Workflow.Activities.find((a) => a.id === node.fromId);
          return !fromNode ? [node] : [];
        }
      });

      newNodes.forEach((n) => {
        model.addNodeData(n);
      });

      this.cdr.detectChanges();
    }
  }

  private initDecorations() {
    let name = "Unknown";
    if (this.workflowId && this.workflowTable === "Workflow" && this.workflowData) {
      name = this.workflowData.Workflow.Name;
    } else {
      name = this.workflowData.Workflow.Name;
    }

    if (this.isEdit) {
      let checkedOutString: string = "";

      if (!this.isCheckedOut) {
        checkedOutString = "(Not checked out)";
      } else {
        if (this.isCheckedOutByThisUser === false) {
          checkedOutString = `(Checked out by ${this.checkedOutBy})`;
        }
      }
      this.title = `Workflow <em>${name} ${checkedOutString}</em>`;
    } else {
      this.title = `Workflow <em>${name}</em>`;
    }
    this.title$.emit(this.title);
  }

  private initOverview(): go.Overview {
    return $$(go.Overview, {
      box: $$(go.Part, {
          selectable: true,
          selectionObjectName: 'BOXSHAPE',
          locationObjectName: 'BOXSHAPE',
          resizeObjectName: 'BOXSHAPE',
          cursor: 'move',
          selectionAdorned: false,
        },
        $$(go.Shape, {
          stroke: 'darkorange',
          strokeWidth: 2,
          strokeDashArray: [4, 8],
          fill: 'transparent',
          name: 'BOXSHAPE',
        }),
      ),
    });
  }

  private initControls(): go.Diagram {
    const dia = $$(go.Diagram, {
      isReadOnly: true,
      allowResize: false,
      allowZoom: false,
      allowHorizontalScroll: false,
      allowVerticalScroll: false,
      "draggingTool.isEnabled": false,
      "panningTool.isEnabled": false,
      "dragSelectingTool.isEnabled": false,
      'undoManager.isEnabled': false,
      "toolManager.hoverDelay": 1000,  // how quickly tooltips are shown
    });

    this.gojsControls = dia;

    return dia;
  }

  private async initData() {
    // If we have a workflowId then get the object, if we have a non workflow id then get the workflow for the object
    if (this.workflowId) {
      try {
        if (this.isEdit) {
          this.workflowData = await this.getWorkflowData();
          this.isCheckedOutByThisUser = this.workflowData.isCheckedOutByThisUser;
          this.checkedOutBy = this.workflowData.checkedOutBy;
          this.isCheckedOut = this.workflowData.isCheckedOut;
        } else {
          // Here we need to show the workflow version
          this.workflowData = await this.getWorkflowVersionData();
          this.workflowInstanceID$.emit(this.workflowData.WorkflowInstanceID);
        }
        this.initDecorations();
      } catch (e) {
        console.error("WorkflowPageComponent.initData", e);
      }
    }
  }

  backToWorkflow() {
    Navigate.to(
      {
        table:  "Workflow",
        id:      this.workflowId
      }
    );
  }

  private linkOverviewToDiagram() {
    if (this.observedDiagram) {
      return;
    }
    this.observedDiagram = this.gojsDiagramComponent.diagram;
    this.cdr.detectChanges();
  }

  private initControlButtons() {
    this.gojsControls.add(
      $$(go.Node, "Auto",
        { selectable: false, movable: false},
        $$(go.Panel, "Table",
          $$(go.RowColumnDefinition, { width: 25 }),
          $$(go.RowColumnDefinition, { isRow: true, height: 25 }),
          $$("Button",
            {
              row: 0,
              column: 0,
              margin: 0,
              toolTip: $$("ToolTip", $$(go.TextBlock, { margin: 4, text: "Zoom in" })),
              click: () => { this.zoomIn() },
              "ButtonBorder.stroke": null,
              "ButtonBorder.fill": null,
            },
            $$(go.TextBlock, { font: '16pt Material Symbols Outlined', stroke: '#425175', text: 'zoom_in'} )),
          $$("Button",
            {
              row: 0,
              column: 3,
              margin: 0,
              toolTip: $$("ToolTip", $$(go.TextBlock, { margin: 4, text: "Zoom out" })),
              click: () => { this.zoomOut() },
              "ButtonBorder.stroke": null,
              "ButtonBorder.fill": null,
            },
            $$(go.TextBlock, { font: '16pt Material Symbols Outlined', stroke: '#425175', text: 'zoom_out' } )),
          $$("Button",
            {
              row: 1,
              column: 1,
              margin: 0,
              toolTip: $$("ToolTip", $$(go.TextBlock, { margin: 4, text: "Reset" })),
              click: () => { this.resetDiagram() },
              "ButtonBorder.stroke": null,
              "ButtonBorder.fill": null,
            },
            $$(go.TextBlock, { font: '16pt Material Symbols Outlined', stroke: '#425175', text: 'refresh' })),
          $$("Button",
            {
              row: 2,
              column: 0,
              margin: 0,
              toolTip: $$("ToolTip", $$(go.TextBlock, { margin: 4, text: "Reset zoom" })),
              click: () => { this.resetZoom() },
              "ButtonBorder.stroke": null,
              "ButtonBorder.fill": null,
            },
            $$(go.TextBlock, { font: '16pt Material Symbols Outlined', stroke: '#425175', text: 'target' })),
          $$("Button",
            {
              row: 2,
              column: 3,
              margin: 0,
              toolTip: $$("ToolTip", $$(go.TextBlock, { margin: 4, text: "Zoom to fit" })),
              click: () => { this.zoomToFit() },
              "ButtonBorder.stroke": null,
              "ButtonBorder.fill": null,
            },
            $$(go.TextBlock, { font: '16pt Material Symbols Outlined', stroke: '#425175', text: 'zoom_in_map' })),
        )
      )
    );
  }

  private zoomIn() {
    this.gojsDiagram.commandHandler.increaseZoom();
  }

  private zoomOut() {
    this.gojsDiagram.commandHandler.decreaseZoom();
  }

  private resetZoom() {
    this.gojsDiagram.commandHandler.resetZoom();
  }

  private zoomToFit() {
    this.gojsDiagram.commandHandler.zoomToFit();
    // The line below will fit the entire document which means that a single node will be enormous.
    // this.gojsDiagram.zoomToRect(this.gojsDiagram.documentBounds);
  }

  private resetDiagram() {
    this.removeAllNodes();
    this.removeAllLinks();
    this.displayDiagram();
    this.clearTransitionsPalette();
    this.displayTransitionsPalette();
  }

  private removeAllNodes() {
    const model = this.getModel();
    if (model.nodeDataArray) {
      model.nodeDataArray
        .map((n) => n.key)
        .forEach((k) => {
          model.removeNodeData(model.findNodeDataForKey(k));
        });
    }
  }

  private removeAllLinks() {
    const model = this.getModel();
    if (model.linkDataArray) {
      model.linkDataArray
        .map((n) => n.key)
        .forEach((k) => {
          model.removeLinkData(model.findLinkDataForKey(k));
        });
    }
  }

  private prepareDiagram() {
    const dia = this.gojsDiagram;

    // Customise the key handling
    dia.commandHandler.doKeyDown = () => {
      const e = dia.lastInput;
      const cmd = dia.commandHandler;
      if (e.key === "Backspace" || e.key === "Del") {  // Disable the auto delete on delete button
        return;
      } else {
        // call base method with no arguments
        go.CommandHandler.prototype.doKeyDown.call(cmd);
      }
    };

    dia.toolManager.hoverDelay = 500;

    dia.toolManager.draggingTool.isEnabled = true;
    dia.toolManager.draggingTool.isGridSnapEnabled = true;

    dia.toolManager.dragSelectingTool.isEnabled = true;

    if (this.isEdit) {
      dia.grid = $$(go.Panel, "Grid",
        {gridCellSize: new go.Size(10, 10)},
        $$(go.Shape, "LineH", {strokeDashArray: [0.5, 14.5], strokeWidth: 0.5}),
      );
    }

    // Define the link that is drawn when we drag from a node.
    // You can also define the temporaryFromNode and temporaryToNode.
    dia.toolManager.linkingTool.temporaryLink = $$(go.Link,
      {
        routing: go.Link.AvoidsNodes,
        corner: 10,
        layerName: "Tool"
      },
      $$(go.Shape, { isPanelMain: true, stroke: "red", strokeWidth: 2 }),  // this is the link shape (the line)
      $$(go.Shape, { toArrow: "Standard", stroke: "red", fill: "red" }),   // this is an arrowhead
    );

    dia.model = $$(go.GraphLinksModel,
      {
        linkToPortIdProperty: 'toPort',
        linkFromPortIdProperty: 'fromPort',
        linkKeyProperty: 'key',
        copiesArrays: true,
        copiesArrayObjects: true
      }
    );

    this.initLayout();

    this.defineNodeTemplates();

    this.defineLinkTemplates();
  }

  private initLayout() {
    const dia = this.gojsDiagram;

    dia.layout = $$(go.LayeredDigraphLayout, {
      direction: 0,
      isInitial: false,
      isOngoing: false,
      layerSpacing: 50,
      setsPortSpots: false,
      columnSpacing: 20,
      isViewportSized: true,
    });
  }

  private populateDiagram() {
    this.prepareDiagram();

    const model = this.getModel();

    const newNodes = this.workflowData.Workflow.Activities.map((node) => {
      return this.createNodeDataFromWorkflowActivityDataModel(node, this.workflowData);
    });

    newNodes.forEach((n) => {
      model.addNodeData(n);
    });

    const newLinks = this.workflowData.Workflow.Transitions.map((link) => {
      return this.createLinkDataFromWorkflowTransitionDataModel(link, this.workflowData);
    });

    newLinks.forEach((n) => {
      model.addLinkData(n);
    });

    this.cdr.detectChanges();
  }

  private populatePalette() {
    const model = this.getPaletteModel();

    this.ACTIVITY_DEFINITION_ARRAY.forEach(it => {
      const key = it.Name;
      const iconName = this.ACTIVITY_DEFINITION_MAP[key].Icon;
      const icon = iconName;

        const d: any = {
          key: key,
          iconName: iconName,
          iconFont: this.MI_ICON_FONT,
          icon: icon,
          hasDynamicPorts: this.ACTIVITY_DEFINITION_MAP[key].HasDynamicPorts,
          title: this.ACTIVITY_DEFINITION_MAP[key].Title,
          label: "Label",
          description: "Description",
          linkedStatusLabel: "Linked status",
          category: key,
          id: key,
          nodeColour: this.ACTIVITY_DEFINITION_MAP[key].Color,
          portData: this.getPortDataArray(key),
        };

      model.addNodeData(d);
    });

    this.cdr.detectChanges();
  }

  private definePaletteTemplates() {

    const basicTemplate =
      $$(go.Node, "Spot",
        {
          selectionAdorned: false,
        },

        $$(go.Panel, "Vertical",
          //  Activity
          $$(go.Panel, "Vertical",
            {
              defaultStretch: go.GraphObject.Horizontal,
            },
            $$(go.Panel, "Auto",
              $$(go.Shape, "RoundedRectangle", {
                  fill: "white",
                  strokeWidth: 0
                },
                new go.Binding("fill", "nodeColour")
              ),

              // Icon
              $$(go.TextBlock,
                {
                  name: "ICON_BUTTON",
                  visible: false,
                  stroke: 'white',
                  textAlign: "left",
                  opacity: 1.0,
                  alignment: go.Spot.Left,
                  margin: new go.Margin(5, 2, 2, 5),
                },
                new go.Binding("text", "icon", (t, target) => {
                  const data = target?.part?.data;
                  if (data && data.initialActivity) {
                    return 'play_arrow';
                  } else {
                    return t;
                  }
                }),
                new go.Binding("font", "iconFont"),
                new go.Binding("visible", "icon", (t) => {
                  return !!t;
                })
              ),

              // Title
              $$(go.TextBlock,
                {
                  width: 130,
                  margin: this.MARGIN_525_30,
                  textAlign: "left",
                  font: this.NORMAL_FONT,
                  stroke: 'white',
                  alignment: go.Spot.Left,
                },
                new go.Binding("text", "title")
              ),
            ),
          ),
        ),
      );
    //
    // const approvalTemplate = this.createApprovalNodeTemplate(basicTemplate);
    // const userTransitionTemplate = this.createUserTransitionNodeTemplate(basicTemplate);
    // const relatedTaskTemplate = this.createRelatedTaskNodeTemplate(basicTemplate);
    // const recordGeneratorTemplate = this.createRecordGeneratorNodeTemplate(basicTemplate);

    const nodeTemplateMapConfig = this.ACTIVITY_DEFINITION_ARRAY.map(it => {
      return { key: it.Name, value: (basicTemplate.copyTemplate() as go.Node) };
    });

    const nodeTemplateMap = new go.Map<string, go.Part>(nodeTemplateMapConfig);

    this.gojsPalette.nodeTemplateMap = nodeTemplateMap;
  }

  private defineTransitionPaletteTemplates() {

    const portContextMenu = $$("ContextMenu",
      this.buildContextMenuButton("Edit transition...", async (adornmentData: any, nodeData: any) => {
        const transitionId = adornmentData.transitionId;

        if (transitionId) {
          await this.editTransition(transitionId);
        }
      }),
      this.buildContextMenuButton("Delete transition...", async (adornmentData: any, nodeData: any) => {
        const transitionId = adornmentData.transitionId;

        if (transitionId) {
          await this.deleteTransition(transitionId);
        }
      }),
    );

    const basicTemplate = $$(go.Node, "Spot",
        {
          selectionAdorned: false,
          isShadowed: true,
          shadowBlur: 50,
          toolTip: $$("ToolTip",
                    $$(go.TextBlock, { margin: 4 },
                    new go.Binding("text", "fromId", (val) => {
                      return val ? 'Invalid fromId' : 'Missing fromId';
                    }))
                   )
        },
        $$(go.Panel, 'Auto', {
            contextMenu: this.isEdit ? portContextMenu : null,
          },
          $$(go.Shape, "RoundedRectangle",
            {
              fill: "white",
              stroke: "black",
              strokeWidth: 2
            },
            new go.Binding("stroke", "nodeColour")
          ),
          $$(go.TextBlock, {
              width: 120,
              margin: this.MARGIN_5252,
              text: 'default text',
              font: this.SMALL_NORMAL_FONT
            },
            new go.Binding("text", "label")
          ),
          $$(go.Shape, "Circle", {
              width: 12,
              height: 12,
              portId: '',
              fill: "transparent",
              stroke: 'blue',
              strokeWidth: 2,
              fromLinkable: true,
              fromMaxLinks: 1,
              fromSpot: go.Spot.Right,
              alignment: go.Spot.Right,
              cursor: "pointer",
            },
            new go.Binding("portId", "portId"),
            new go.Binding("stroke", "colour"),
            new go.Binding("fill", "colour"),
          ),
        )
      );

    const nodeTemplateMap = new go.Map<string, go.Part>([
      { key: "", value: basicTemplate},
    ]);

    this.gojsTransitionsPalette.nodeTemplateMap = nodeTemplateMap;
  }

  private buildContextMenuButton(label: string, callback: (adornmentData: any, nodeData: any) => void) {
    return $$("ContextMenuButton",
      $$(go.TextBlock, label, { alignment: go.Spot.Left, margin: 2, stroke: '#425175', font: "normal normal 13px 'Source Sans Pro', 'sans-serif'"}),
      {
        click: (e: go.InputEvent, obj: go.GraphObject) => {
          const adornment = obj.part as go.Adornment;
          const node = adornment.adornedPart as go.Node;
          callback(adornment?.data, node?.data);
        }
      },
    );
  };

  private defineNodeTemplates() {

    // Context menus

    const portContextMenu = $$("ContextMenu",
      this.buildContextMenuButton("Edit transition...", async (adornmentData: any, nodeData: any) => {
        const transitionId = adornmentData.transitionId;
        const fromPort = adornmentData.portId;
        const fromId = nodeData.key;

        if (transitionId) {
          await this.editTransition(transitionId);
        } else {
          const newId = await this.createFixedTransition({
            fromPort: fromPort,
            from: fromId,
            to: null
          });
          if (newId) {
            await this.synchronizeWorkflowData();
            this.updateDiagramFromModel();
          }
        }
      }),
      this.buildContextMenuButton("Delete transition...", async (adornmentData: any, nodeData: any) => {
        const transitionId = adornmentData.transitionId;

        if (transitionId) {
          await this.deleteTransition(transitionId);
        }
      }),
    );

    const nodeContextMenu = $$("ContextMenu",
      this.buildContextMenuButton("Edit activity...", async (adornmentData: any, nodeData: any) => {
        const activityId = nodeData.key;
        const tableName = nodeData.category;

        if (activityId && tableName) {
          await this.editActivity(activityId, tableName);
        }
      }),
      this.buildContextMenuButton("Delete activity...", async (adornmentData: any, nodeData: any) => {
        const activityId = nodeData.key;
        const tableName = nodeData.category;

        if (activityId && tableName) {
          await this.deleteActivity(activityId, tableName);
        }
      }),
      this.buildContextMenuButton("Set initial activity...", async (adornmentData: any, nodeData: any) => {
        const activityId = nodeData.key;

        if (activityId) {
          await this.setInitialActivity(activityId);
        }
      }),
    );

    const basicTemplate = this.getBasicTemplateNew(nodeContextMenu, portContextMenu);
    //
    // const approvalTemplate = this.createApprovalNodeTemplate(basicTemplate);
    // const userTransitionTemplate = this.createUserTransitionNodeTemplate(basicTemplate);
    // const relatedTaskTemplate = this.createRelatedTaskNodeTemplate(basicTemplate);
    // const recordGeneratorTemplate = this.createRecordGeneratorNodeTemplate(basicTemplate);

    const nodeTemplateMapConfig = this.ACTIVITY_DEFINITION_ARRAY.map(it => {
      return { key: it.Name, value: (basicTemplate.copyTemplate() as go.Node) };
    });

    nodeTemplateMapConfig.push({ key: "", value: basicTemplate});

    const nodeTemplateMap = new go.Map<string, go.Part>(nodeTemplateMapConfig);
    // ,
    //
    //   { key: "", value: basicTemplate},
    // ]);

    this.gojsDiagram.nodeTemplateMap = nodeTemplateMap;
  }

  private getBasicTemplateNew(nodeContextMenu: go.Adornment, portContextMenu: go.Adornment) {
    const editActivityNode = (e, node) => {
      this.editActivity(node.key, node.category);
    };

    const editTransitionNode = (e, node) => {
      this.editTransition(node.key);
    };

    return $$(go.Node, 'Spot',
      {
        contextMenu: this.isEdit ? nodeContextMenu : null,
        selectionAdorned: false,
        mouseEnter: (e: go.InputEvent, obj: go.Node) => {
          obj.shadowBlur = 20;

          if (this.isEdit) {
            // (obj.findObject('DELETE_BUTTON') as go.TextBlock).opacity = 1.0;
            // (obj.findObject('ICON_BUTTON') as go.TextBlock).opacity = 0.0;
          }

          obj.ports.each((p) => {
            const port = p as go.Shape;
            let highlight = true;
            if (port.fromLinkable) {
              let hasLink = false;
              obj.findLinksOutOf().each((p) => {
                if (p.fromPort == port) {
                  hasLink = true;
                }
              });
              if (hasLink) {
                highlight = false;
              }
            }
            if (highlight) {
              port.strokeWidth = 3;
            }
          });
        },
        mouseLeave: (e, obj: go.Node) => {
          obj.shadowBlur = 50;

          if (this.isEdit) {
            // (obj.findObject('DELETE_BUTTON') as go.TextBlock).opacity = 0.0;
            // (obj.findObject('ICON_BUTTON') as go.TextBlock).opacity = 1.0;
          }

          obj.ports.each((p) => {
            const port = p as go.Shape;
            port.strokeWidth = 2;
          });
        }
      },
      new go.Binding('location', 'loc', go.Point.parse).makeTwoWay(go.Point.stringify),
      $$(go.Panel, 'Horizontal',
        $$(go.Panel, 'Vertical',
          //  Activity
          $$(go.Panel, 'Vertical',
            {
              defaultStretch: go.GraphObject.Horizontal,
            },
            $$(go.Panel, 'Auto',

              $$(go.Shape, 'RoundedRectangle', {
                  fill: 'gray',
                  stroke: "gray",
                  width: this.BASE_ACTIVITY_WIDTH,
                  strokeWidth: 1,
                  doubleClick: editActivityNode
                },
                new go.Binding('fill', 'nodeColour'),
                new go.Binding('stroke', 'nodeColour'),
                new go.Binding('width', 'width'),
                new go.Binding('key', 'key'),
                new go.Binding('category', 'category')
              ),

              // In port
              $$(go.Shape, 'Circle', {
                width: 10,
                height: 10,
                portId: 'input',
                fill: 'transparent',
                stroke: 'transparent',
                strokeWidth: 1,
                alignment: go.Spot.Left,
                toLinkable: true,
                toSpot: go.Spot.TopLeft,
                cursor: 'pointer',
                margin: 1
              }),

              // Icon
              $$(go.TextBlock,
                {
                  name: 'ICON_BUTTON',
                  visible: false,
                  stroke: 'white',
                  textAlign: 'left',
                  opacity: 1.0,
                  alignment: go.Spot.Left,
                  margin: new go.Margin(4, 4, 0, 5),
                  doubleClick: editActivityNode
                },
                new go.Binding('text', 'icon', (t, target) => {
                  const data = target?.part?.data;
                  if (data && data.initialActivity) {
                    return 'play_arrow';
                  } else {
                    return t;
                  }
                }),
                new go.Binding('font', 'iconFont'),
                new go.Binding('key', 'key'),
                new go.Binding('category', 'category'),
                new go.Binding('visible', 'icon', (t) => {
                  return !!t;
                })),

              $$(go.TextBlock,
                {
                  width: this.BASE_ACTIVITY_WIDTH,
                  margin: new go.Margin(3, 2, 2, 60),
                  stroke: 'white',
                  font: this.NORMAL_FONT_LARGE,
                  doubleClick: editActivityNode
                },
                new go.Binding('text', 'label'),
                new go.Binding('width', 'width'),
                new go.Binding('key', 'key'),
                new go.Binding('category', 'category')
              ),

              // Icon
              $$(go.TextBlock,
                {
                  name: "VISITED",
                  visible: false,
                  stroke: 'white',
                  textAlign: "right",
                  opacity: 1.0,
                  alignment: go.Spot.TopRight,
                  margin: 5,
                  text: "",
                  font: this.MI_ICON_FONT
                },
                new go.Binding("visible", "isCurrentOrVisited"),
                new go.Binding("text", "isCurrent", (value, node) => {
                    return value ? "arrow_circle_right" : "check";
                })
              ),

              // Add button for dynamically generated transitions
              $$(go.Shape, 'LogicXor', {
                  width: 14,
                  height: 14,
                  stroke: 'white',
                  strokeWidth: 2,
                  background: 'transparent',
                  fromLinkable: true,
                  cursor: 'pointer',
                  alignment: go.Spot.Right,
                  click: async (e: go.InputEvent, obj) => {
                    if (this.isEdit) {
                      let node = obj.part;
                      let data = node.data;
                      const newId = await this.createDynamicTransition(data);
                      if (newId) {
                        await this.synchronizeWorkflowData();
                        this.updateDiagramFromModel();
                      }
                    }
                  },
                },
                new go.Binding('visible', 'hasDynamicPorts', (t) => {
                  return !!t && this.isEdit;
                })
              ),
            ),
          ),

          // Ports
          $$(go.Panel, 'Vertical',
            new go.Binding('itemArray', 'portData'),
            {
              name: 'PORT_PANEL',
              itemTemplate: $$(go.Panel, 'Auto', {
                  contextMenu: this.isEdit ? portContextMenu : null,
                },
                $$(go.Shape, 'RoundedRectangle',
                  {
                    fill: 'white',
                    stroke: 'black',
                    strokeWidth: 1,
                    width: this.BASE_ACTIVITY_WIDTH - 20,
                    margin: new go.Margin(0,0,0,20),
                    doubleClick: editTransitionNode
                  },
                  new go.Binding('stroke', 'nodeColour'),
                  new go.Binding('key', 'transitionId'),
                ),
                $$(go.TextBlock, {
                    margin: this.MARGIN_5252,
                    text: 'default text',
                    font: this.SMALL_NORMAL_FONT,
                    textAlign: 'left',
                    alignment: go.Spot.Left,
                    doubleClick: editTransitionNode
                  },
                  new go.Binding('text', 'text'),
                  new go.Binding('key', 'transitionId'),
                ),
                $$(go.Shape, 'Circle', {
                    width: 12,
                    height: 12,
                    portId: '',
                    fill: 'transparent',
                    stroke: 'black',
                    strokeWidth: 2,
                    fromLinkable: true,
                    fromMaxLinks: 1,
                    fromSpot: go.Spot.Right,
                    alignment: go.Spot.Right,
                    cursor: 'pointer',
                  },
                  new go.Binding('portId', 'portId'),
                  new go.Binding('stroke', 'colour'),
                ),
                $$(go.Panel, 'Auto', {
                    alignment: go.Spot.Right,
                  },
                  $$(go.Shape, 'RoundedRectangle', {
                    fill: 'red'
                  }),
                  $$(go.TextBlock, {
                      textAlign: 'center',
                      verticalAlignment: go.Spot.Center,
                      font: this.BADGE_FONT
                    },
                    new go.Binding('text', 'history')
                  ),
                  new go.Binding('visible', 'history', (val) => {
                    return !!val;
                  })
                )
              )
            },
          ),
        ),
        $$(go.Shape, "RoundedRectangle", {
            // This is an empty rectangle to allow the bounds of the links to
            // be included in the line routing calculations
            fill: "transparent",
            stroke: "gray",
            strokeWidth: 0,
            width: 170,
            height: 10
          },
        ),
      ),
    );
  }

  //
  // private createApprovalNodeTemplate(basicTemplate: go.Node): go.Node {
  //   return basicTemplate.copyTemplate() as go.Node;
  // }
  //
  // private createRecordGeneratorNodeTemplate(basicTemplate: go.Node): go.Node {
  //   return basicTemplate.copyTemplate() as go.Node;
  // }
  //
  // private createRelatedTaskNodeTemplate(basicTemplate: go.Node): go.Node {
  //   return basicTemplate.copyTemplate() as go.Node;
  // }
  //
  // private createUserTransitionNodeTemplate(basicTemplate: go.Node): go.Node {
  //   return basicTemplate.copyTemplate() as go.Node;
  // }

  // No longer being used but it might be useful in the future, it allows you to position
  // anything on a link at the mouse position.
  //
  //   const deleteButton = (link.findObject("LINK_DELETE_BUTTON") as go.TextBlock);
  //   const {segment, fraction} = this.positionLinkLabel(link);
  //   deleteButton.segmentIndex = segment;
  // deleteButton.segmentFraction = fraction;
  // deleteButton.opacity = 1.0;

  private positionLinkLabel(link: go.Link) {
    let p = this.gojsDiagram.lastInput.documentPoint.copy();

    const points: go.Point[] = [];
    link.points.each((p) => points.push(p));
    const closestSegment = link.findClosestSegment(p);
    const a = points[closestSegment];
    const b = points[closestSegment + 1];

    const atob = { x: b.x - a.x, y: b.y - a.y };
    const atop = { x: p.x - a.x, y: p.y - a.y };
    const len = atob.x * atob.x + atob.y * atob.y;
    const dot = atop.x * atob.x + atop.y * atob.y;
    const t = Math.min( 1, Math.max( 0, dot / len ) );

    return {segment: closestSegment, fraction: t};
  }

  private defineLinkTemplates() {
    const baseTemplate = this.getExperimentalWorkflowLinkTemplate();

    const customLinkTemplate = $$(CustomLink,
      {
        routing: go.Link.AvoidsNodes,
        corner: 4,
        curve: go.Link.JumpGap,
        reshapable: true,
        resegmentable: true,
        relinkableFrom: true,
        relinkableTo: true
      },
      new go.Binding("points").makeTwoWay(),
      $$(go.Shape, { stroke: "#2F4F4F", strokeWidth: 2 })
    );

    const linkTemplateMap = new go.Map<string, go.Link>([
      { key: "WorkflowTransition", value: baseTemplate},
      { key: "", value: baseTemplate},
    ]);

    this.gojsDiagram.linkTemplateMap = linkTemplateMap;
  }

  private getExperimentalWorkflowLinkTemplate() {

    const getTextForItem = (t, target) => {
      const data = target?.part?.data;

      if (data && data.to) {
        const activity = this.workflowData.Workflow.Activities.find(it => it.id === data.to);
        return activity?.Name;
      } else {
        return t;
      }
    };

    const getTextWidth = (text) => {
      return this.getTextWidth(text, this.BOLD_FONT);
    };

    const getMinContainerWidth = (minWidth, padding, t, target) => {
      const text = getTextForItem(t, target);
      return Math.max(minWidth, getTextWidth(text) + padding);
    };

    return $$(go.Link,
      {
        selectionAdorned: false,
        routing: go.Link.Orthogonal, /* go.Link.AvoidsNodes always reroutes links when you don't want it to */
        corner: 10,
        curve: go.Link.JumpGap,
        reshapable: true,
        resegmentable: true,
        adjusting: go.Link.End,
        mouseEnter: (e: go.InputEvent, link: go.Link) => {
          const line = (link.findObject('LINK') as go.Shape);
          const arrow = (link.findObject('ARROW') as go.Shape);
          const rect = (link.findObject('LINK_RECT') as go.Shape);
          const text = (link.findObject('LINK_TEXT') as go.TextBlock);

          text.font = this.BOLD_FONT;

          rect.strokeWidth = 2;
          line.strokeWidth = 4;

          arrow.strokeWidth = 3;
          line.stroke = this.LINK_ACTIVE_COLOUR;
          arrow.stroke = this.LINK_ACTIVE_COLOUR;
          arrow.fill = this.LINK_ACTIVE_COLOUR;
        },
        mouseLeave: (e: go.InputEvent, link: go.Link) => {
          const line = (link.findObject('LINK') as go.Shape);
          const arrow = (link.findObject('ARROW') as go.Shape);
          const rect = (link.findObject('LINK_RECT') as go.Shape);
          const text = (link.findObject('LINK_TEXT') as go.TextBlock);

          text.font = this.NORMAL_FONT;

          rect.strokeWidth = 1;
          line.strokeWidth = 2;
          line.stroke = this.LINK_INACTIVE_COLOUR;
          arrow.stroke = this.LINK_INACTIVE_COLOUR;
          arrow.fill = this.LINK_INACTIVE_COLOUR;
          arrow.strokeWidth = 2;
        }
      },
      new go.Binding('points').makeTwoWay(),
      $$(go.Shape, {
        isPanelMain: true,
        stroke: this.LINK_INACTIVE_COLOUR,
        strokeWidth: 2,
        name: 'LINK'
      }),      // this is the link shape (the line)
      $$(go.Shape, {
        toArrow: 'Standard',
        stroke: this.LINK_INACTIVE_COLOUR,
        fill: this.LINK_INACTIVE_COLOUR,
        strokeWidth: 2,
        name: 'ARROW'
      }),   // this is an arrowhead
      $$(go.Panel, "Position",
        {
          segmentIndex: 0,
          segmentFraction: 0
        },
        $$(go.Panel, 'Position',
          new go.Binding("position", "label", (t, target) => {
            const baseOffset = 180;
            const padding = 35;
            const minWidth = baseOffset - padding;

            const containerWidth = getMinContainerWidth(minWidth, padding, t, target);

            if (containerWidth === minWidth) { return new go.Point(baseOffset, 0); }

            const offset = containerWidth - minWidth;

            return new go.Point((baseOffset + (offset)) - 5, 0);
          }),
          $$(go.Shape, 'RoundedRectangle', {
              fill: 'white',
              stroke: "gray",
              opacity: 1,
              width: 0,
              strokeWidth: 1,
              height: 22,
              position: new go.Point(0, 0),
              name: "LINK_RECT",
            },
            new go.Binding("width", "label", (t, target) => {
              return getMinContainerWidth(150, 35, t, target);
            }),
          ),
          $$(go.TextBlock, {
            name: 'LINK_ICON',
            text: 'arrow_circle_right',
            font: this.MI_ICON_FONT,
            stroke: this.LINK_ACTIVE_COLOUR,
            position: new go.Point(5, 4)
          }),
          $$(go.TextBlock,
            {
              name: 'LINK_TEXT',
              font: this.NORMAL_FONT,
              stroke: this.LINK_ACTIVE_COLOUR,
              position: new go.Point(23, 6),
              width: 130,
            },
            new go.Binding("text", "label", getTextForItem),
            new go.Binding("width", "label", (t, target) => {
              return getMinContainerWidth(130, 10, t, target);
            }),
          ),
        ),
      )// this is the link shape (the line)
    );
  }

// Sync management

  private async getWorkflowData() {
    const response: any = await
      this.requestService
        .createRequest()
        .controller("servicely.core.Custom")
        .method("getWorkflowDiagramDetails")
        .table(this.workflowTable)
        .data({ id: this.workflowId })
        .execute();

    return response.data;
  }

  private async getWorkflowVersionData() {
    const response: any = await
      this.requestService
        .createRequest()
        .controller("servicely.core.Custom")
        .method("getWorkflowVersionDiagramDetails")
        .table(this.workflowTable)
        .data({ id: this.workflowId, table: this.workflowTable })
        .execute();

    return response.data;
  }

  // Get the latest data from the server and update our local copy to match.
  // We then build the goJS model and which drives the display of the diagram.
  private async synchronizeWorkflowData() {

    const model = this.getModel();

    const newData: WorkflowDetailsDataModel = await this.getWorkflowData();

    const nodesToAdd: WorkflowActivityDataModel[] = [];
    const nodesToRemove: WorkflowActivityDataModel[] = [];
    const nodesToUpdate: WorkflowActivityDataModel[] = [];

    this.workflowData.Workflow.Activities.forEach((node) => {
      const newNode = newData.Workflow.Activities.find((newNode) => {
        return node.id === newNode.id;
      });
      if (!newNode) {
        nodesToRemove.push(node);
      } else {
        nodesToUpdate.push(newNode);
      }
    });

    newData.Workflow.Activities.forEach((newNode) => {
      const oldNode = this.workflowData.Workflow.Activities.find((node) => {
        return node.id === newNode.id;
      });
      if (!oldNode) {
        nodesToAdd.push(newNode);
      }
    });

    const linksToAdd: WorkflowTransitionDataModel[] = [];
    const linksToRemove: WorkflowTransitionDataModel[] = [];
    const linksToUpdate: WorkflowTransitionDataModel[] = [];
    const linksToRemoveButNotTransitions: WorkflowTransitionDataModel[] = [];

    this.workflowData.Workflow.Transitions.forEach((link) => {
      const newLink = newData.Workflow.Transitions.find((newLink) => {
        return link.id === newLink.id;
      });
      if (!newLink || newLink.ToActivity === null || newLink.FromActivity === null) {
        if (!newLink) {
          linksToRemove.push(link);
        } else if (newLink.ToActivity === null || newLink.FromActivity === null) {
          linksToRemoveButNotTransitions.push(newLink);
        }
      } else {
        linksToUpdate.push(newLink);
      }
    });

    newData.Workflow.Transitions.forEach((newLink) => {
      const oldLink = this.workflowData.Workflow.Transitions.find((link) => {
        return link.id === newLink.id;
      });
      if (!oldLink && newLink.FromActivity && newLink.ToActivity) {
        linksToAdd.push(newLink);
      } else {
        if (newLink.FromActivity) {
          const fromActivity = newData.Workflow.Activities.find((newNode) => newNode.id === newLink.FromActivity);
          if (!fromActivity) {
            linksToRemoveButNotTransitions.push(newLink);
          }
          const toActivity = newData.Workflow.Activities.find((newNode) => newNode.id === newLink.ToActivity);
          if (!toActivity) {
            linksToRemoveButNotTransitions.push(newLink);
          }
        }
      }
    });

    nodesToAdd.forEach((node) => {
      const existingNodeData = model.findNodeDataForKey(node.id);
      if (existingNodeData) {
        const newNodeData = this.createNodeDataFromWorkflowActivityDataModel(node, newData);
        Object.assign(existingNodeData, newNodeData);
      } else {
        const newNodeData = this.createNodeDataFromWorkflowActivityDataModel(node, newData);
        model.addNodeData(newNodeData);
      }
    });

    nodesToRemove.forEach((node) => {
      const existingNodeData = model.findNodeDataForKey(node.id);
      if (existingNodeData) {
        model.removeNodeData(existingNodeData);
      }
    });

    nodesToUpdate.forEach((node) => {
      const existingNodeData = model.findNodeDataForKey(node.id);
      if (existingNodeData) {
        const newNodeData = this.createNodeDataFromWorkflowActivityDataModel(node, newData);
        Object.assign(existingNodeData, newNodeData);
      }
    });

    linksToAdd.forEach((link) => {
      const existingLinkData = model.findLinkDataForKey(link.id);
      if (existingLinkData) {
        const newLinkData = this.createLinkDataFromWorkflowTransitionDataModel(link, newData);
        Object.assign(existingLinkData, newLinkData);
      } else {
        if (link.FromActivity && link.ToActivity) {
          const newLinkData = this.createLinkDataFromWorkflowTransitionDataModel(link, newData);
          model.addLinkData(newLinkData);
        }
      }
    });

    // These are transitions that have been removed, we need to remove the goJS link.
    // The dynamic ports will take care of themselves.
    linksToRemove.forEach((link) => {
      const existingLinkData = model.findLinkDataForKey(link.id);
      if (existingLinkData) {
        model.removeLinkData(existingLinkData);
      }
    });

    // These are transitions that have no ToActivity. We need to remove the link but not the port.
    linksToRemoveButNotTransitions.forEach((link) => {
      const existingLinkData = model.findLinkDataForKey(link.id);
      if (existingLinkData) {
        model.removeLinkData(existingLinkData);
      }
    });

    linksToUpdate.forEach((link) => {
      const existingLinkData = model.findLinkDataForKey(link.id);
      if (existingLinkData) {
        const newLinkData = this.createLinkDataFromWorkflowTransitionDataModel(link, newData);
        Object.assign(existingLinkData, newLinkData);
      }
    });

    this.workflowData = newData;
  }

  // Node and Link model management

  private getPortDataArray(category: string) {
    const activity = this.ACTIVITY_DEFINITION_MAP[category];

    return activity ? cloneDeep(activity.PortDataArray) : [];
  }

  private createNodeDataFromWorkflowActivityDataModel(node: WorkflowActivityDataModel, data: WorkflowDetailsDataModel) {
    const id = node.id;

    const table = node.TableClass;
    const label = node.Name;
    const description = node.Description;
    const linkedStatus = node.LinkedStatus;
    const linkedStatusI18nKey = node.LinkedStatusI18nKey;
    const iconName = this.ACTIVITY_DEFINITION_MAP[table].Icon;
    const icon = iconName;
    const hasDynamicPorts = this.ACTIVITY_DEFINITION_MAP[table].HasDynamicPorts;
    const title = this.ACTIVITY_DEFINITION_MAP[table].Title;
    let nodeColour = this.ACTIVITY_DEFINITION_MAP[table].Color;

    const stripPX = (s: string): string => {
      return s ? (s.endsWith('px') ? s.slice(0, -2) : s) : "";
    };

    const leftPosition = stripPX(node.LeftPosition);
    const topPosition = stripPX(node.TopPosition);
    const loc = `${leftPosition} ${topPosition}`;

    const initialActivity = id === data.Workflow.InitialActivity;

    const historyCurrentStatus = !this.isEdit && id === data.History.CurrentStatus;
    const historyVisitedStatus = !this.isEdit && data.History.Entries.find((e) => e.FromActivity === id || e.ToActivity === id);

    //
    // if (historyVisitedStatus) {
    //   nodeColour = this.HISTORY_ACTIVITY_VISITED_COLOUR;
    // }
    // if (historyCurrentStatus) {
    //   nodeColour = this.HISTORY_CURRENT_STATUS_COLOUR;
    // }

    let portData = this.getPortDataArray(table);

    if (hasDynamicPorts) {
      const relevantTransitions = data.Workflow.Transitions.filter((t) => t.FromActivity === id);

      portData = relevantTransitions.map((link) => {
        const transitionId = link.id;
        const label = this.i18nService.getMessage(link.I18nKey);
        const fromId = link.FromActivity;
        const fromData = this.getNodeDataModelById(fromId);
        const { fromPort, toPort } = this.determinePortIds(link);

        let history = null;

        if (!this.isEdit) {
          const indices = this.getTransitionOnlyHistoryEntriesForTransition(data.History.Entries, transitionId);

          if (indices.length > 0) {
            history = indices.join(" | ");
          }
        }

        return {
          text: label,
          portId: fromPort,
          colour: 'black',
          nodeColour: nodeColour,
          transitionId: link.id,
          history: history
        };
      });

    } else {
      const relevantTransitions = data.Workflow.Transitions.filter((t) => t.FromActivity === id);

      relevantTransitions.forEach((link) => {
        const code = link.Code;
        const portItemData = portData.find((p) => p.portId === code);

        let history = null;

        if (!this.isEdit) {
          const indices = this.getTransitionOnlyHistoryEntriesForTransition(data.History.Entries, link.id);

          if (indices.length > 0) {
            history = indices.join(" | ");
          }
        }

        if (portItemData) {
          portItemData.transitionId = link.id;
          portItemData.history = history;
        }
      });
    }

    const nodeLabel = this.i18nService.getMessage(linkedStatusI18nKey);
    const nodeData =  {
      key: id,
      category: table,
      label: label,
      description: description,
      linkedStatus: linkedStatus,
      linkedStatusLabel: nodeLabel,
      id: id,
      icon: icon,
      iconName: iconName,
      iconFont: this.MI_ICON_FONT,
      hasDynamicPorts: hasDynamicPorts,
      title: title,
      nodeColour: nodeColour,
      initialActivity: initialActivity,
      portData: portData,
      loc: loc,
      width: Math.max(this.BASE_ACTIVITY_WIDTH, (this.getTextWidth(label, this.NORMAL_FONT_LARGE) + 50)),
      isCurrent: historyCurrentStatus === true, // During the display phase, denotes the workflow action that is currently executing
      isVisited: historyVisitedStatus != null,  // During the display phase, denotes whether the Workflow action has been visited,
      isCurrentOrVisited: this.isEdit === false && (historyCurrentStatus === true || historyVisitedStatus != null)
     };

    return nodeData;
  }

  private getTextWidth(text, font) {
    // re-use canvas object for better performance
    const canvas = window["__canvasMeasure"] || (window["__canvasMeasure"] = document.createElement("canvas"));
    const context = canvas.getContext("2d");
    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
  }

  private createLinkDataFromWorkflowTransitionDataModel(link: WorkflowTransitionDataModel, data: WorkflowDetailsDataModel) {
    const table = link.TableClass;
    const id = link.id;
    const fromId = link.FromActivity;
    const toId = link.ToActivity;
    const code = link.Code;
    const points = link.Points;

    const label = this.i18nService.getMessage(link.I18nKey);
    const { fromPort, toPort } = this.determinePortIds(link);

    let history = null;

    if (!this.isEdit) {
      const indices = this.getTransitionOnlyHistoryEntriesForTransition(data.History.Entries, id);

      if (indices.length > 0) {
        history = indices.join(" | ");
      }
    }

    const pointList = this.pointArrayStringToPointList(points);

    if (!fromId || !toId) {
      return null;
    } else {
      return {
        key: id,
        category: table,
        label: label,
        from: fromId,
        to: toId,
        table: table,
        id: id,
        fromPort: fromPort,
        toPort: toPort,
        history: history,
        points: pointList
      };
    }
  }

  private getTransitionOnlyHistoryEntriesForTransition(Entries: WorkflowHistoryDataModel[], linkID: string) {
    const indices = Entries
      .filter(it => it.Transition != null)
      .sort((a,b) => a.UpdatedOn - b.UpdatedOn);

    return indices.flatMap((e, index) => {
      return e.Transition === linkID ? [index + 1] : [];
    });
  }

  private createNodeDataFromWorkflowTransitionDataModelForTransitionPalette(link: WorkflowTransitionDataModel, data: WorkflowDetailsDataModel) {
    const table = link.TableClass;
    const id = link.id;
    const fromId = link.FromActivity;
    const toId = link.ToActivity;
    const code = link.Code;
    const label = this.i18nService.getMessage(link.I18nKey);
    const {fromPort, toPort} = this.determinePortIds(link);

    return {
      key: id,
      category: table,
      label: label,
      table: table,
      fromId: fromId,
      toId: toId,
      code: code,
      fromPort: fromPort,
      toPort: toPort,
      id: id,
      transitionId: id,
      colour: (!fromId ? 'black' : 'red')
    };
  }

  // Data model management
  private getNodeDataModelById(id: string): WorkflowActivityDataModel {
    if (this.workflowData) {
      return this.workflowData.Workflow.Activities.find((a) => a.id === id) || null;
    } else {
      return null;
    }
  }

  private getLinkDataModelById(id: string): WorkflowTransitionDataModel {
    if (this.workflowData) {
      return this.workflowData.Workflow.Transitions.find((a) => a.id === id) || null;
    } else {
      return null;
    }
  }

  private getNodeModelById(id: string): any {
    let result = null;
    const model = this.getModel();
    if (model.nodeDataArray) {
      result = model.nodeDataArray.find((n) => n.key === id);
    }
    return result;
  }

  private getLinkModelById(id: string): any {
    let result = null;
    const model = this.getModel();
    if (model.linkDataArray) {
      result = model.linkDataArray.find((n) => n.key === id);
    }
    return result;
  }

  // Indicate the portIds for a link. Only the fromPort is used.
  private determinePortIds(link: WorkflowTransitionDataModel): {fromPort: string, toPort: string} {
    const fromNode = this.getNodeDataModelById(link.FromActivity);
    const toNode = this.getNodeDataModelById(link.ToActivity);
    const code = link.Code;
    let fromPort = "";
    let toPort = "input";

    if (fromNode) {
      fromPort = code;
    }

    if (toNode) {
      switch (toNode.TableClass) {
        default:
          toPort = "input";
      }
    }
    return {fromPort: fromPort, toPort: toPort};
  }

  // Edit an existing transition
  private async editTransition(transitionId: any) {
    if (transitionId) {
      this.displayRightSlideInWindow(transitionId, "WorkflowTransition");
    }
  }

  // Edit an existing activity
  private async editActivity(activityId: string, tableName: string) {
    if (activityId && tableName) {
      this.displayRightSlideInWindow(activityId, tableName);
    }
  }

  // Delete a subclass of WorkflowActivity
  private async deleteActivity(activityId: string, tableName: string) {
    await this.deleteRecord(activityId, tableName);

    await this.synchronizeWorkflowData();
    this.updateDiagramFromModel();
  }

  // Set the initial activity of the workflow
  private async setInitialActivity(activityId: string) {
    const data = {
      InitialActivity: activityId
    };
    await this.updateRecord(data, this.workflowId, "Workflow");

    await this.synchronizeWorkflowData();
    this.updateDiagramFromModel();
  }

  // Delete a link in the diagram by setting the ToActivity to null.
  private async deleteLink(transitionId: string) {
    await this.setTransitionToActivity(transitionId, null);

    await this.synchronizeWorkflowData();
    this.updateDiagramFromModel();
  }

  // Delete a transition
  private async deleteTransition(transitionId: string) {
    await this.deleteRecord(transitionId, "WorkflowTransition");

    await this.synchronizeWorkflowData();
    this.updateDiagramFromModel();
  }

  // Set the ToActivity end of a transition
  private async setTransitionToActivity(transitionId: string, toId: string) {
    const data = {ToActivity: toId};
    if (toId === null) {
      data["Points"] = null;
    }
    await this.updateRecord(data, transitionId, "WorkflowTransition");
    await this.synchronizeWorkflowData();
    this.updateDiagramFromModel();
  }

  // Create an activity.
  private async createActivity(node: any) {
    const tableName = node.category;
    const loc = go.Point.parse(node.loc);
    const promptResponse: any = await this.prompt("Diagram.AddItem.Prompt.Name.Message");
    const response = promptResponse.response;
    const activityName = promptResponse.data;

    const addPX = (s: number): string => {
      return `${s}px`;
    };

    if (response === "ok" && (window as any).StringUtils.isNotBlank(activityName)) {
      const data = {
        // Description: "Describe here what this workflow activity is about",
        Name: activityName,
        Workflow: this.workflowId,
        TableClass: tableName,
        LeftPosition: addPX(loc.x),
        TopPosition: addPX(loc.y),
      };

      const response: any = await this.createRecord(data, tableName);

      // CJ - Hid this to speed up the workflow. The defaults are fine.
      // this.displayRightSlideInWindow(response.data.id, tableName);

      return response.data.id;
    } else {
      return null;
    }
  }

  // Create a fixed transition attached to a fixed activity.
  private async createFixedTransition(data: any) {
    const fromPort = data.fromPort;
    const fromId = data.from;
    const toId = data.to;

    const sourceActivity = this.getNodeDataModelById(fromId);
    const targetActivity = this.getNodeDataModelById(toId);

    const response = "ok";
    const relationName = sourceActivity.Name + " -> " + targetActivity.Name;

    // const promptResponse: any = await this.prompt("Diagram.AddRelation.Prompt.Name.Message");
    // const response = promptResponse.response;
    // const relationName = promptResponse.data;

    if (response === "ok" && (window as any).StringUtils.isNotBlank(relationName)) {
      const i18nKey = await this.createLocalizedText("WorkflowTransition", relationName);

      const createData = {
        Code: fromPort,
        // Description: "Describe here what this workflow transition is about",
        FromActivity: fromId,
        ToActivity: toId,
        Name: relationName,
        Order: 10,
        Workflow: this.workflowId,
        I18nKey: i18nKey
      };

      const createResponse: any = await this.createRecord(createData, "WorkflowTransition");

      return createResponse.data.id;
    } else {
      return null;
    }
  }

  // Create a dynamic transition attached to a dynamic activity.
  private async createDynamicTransition(nodeModel: any) {

    const promptResponse: any = await this.prompt("Diagram.AddRelation.Prompt.Name.Message");
    const response = promptResponse.response;
    const relationName = promptResponse.data;

    if (response === "ok" && (window as any).StringUtils.isNotBlank(relationName)) {
      const code = relationName.toLowerCase().replace(/ /g, '');
      const i18nKey = await this.createLocalizedText("WorkflowTransition", relationName);
      const data = {
        Code: code,
        // Description: "Describe here what this workflow transition is about",
        FromActivity: nodeModel.key,
        Name: relationName,
        Order: 10,
        Workflow: this.workflowId,
        I18nKey: i18nKey
      };

      const response: any = await this.createRecord(data, "WorkflowTransition");

      // CJ - Hid this to speed up the workflow. The defaults are fine.
      // this.displayRightSlideInWindow(response.data.id, "WorkflowTransition");

      return response.data.id;
    } else {
      return null;
    }
  }

  // Utilities
  private async createRecord(data: any, tableName: string) {
    return this.requestService.createRequest()
      .async(false)
      .controller("servicely.core.System")
      .method("create")
      .table(tableName)
      .data(data)
      .execute();
  }

  private async updateRecord(data: any, id: string, tableName: string) {
    return this.requestService.createRequest()
      .async(false)
      .controller("servicely.core.System")
      .method("update")
      .table(tableName)
      .data({...data, id: id})
      .execute();
  }

  private async deleteRecord(id: any, tableName: string) {
    return this.requestService.createRequest()
      .async(false)
      .controller("servicely.core.System")
      .method("delete")
      .table(tableName)
      .data({id: id})
      .execute();
  }

  private async publishWorkflowVersion(id: any, message: string, publish: boolean) {
    return this.requestService.createRequest()
      .async(false)
      .controller("servicely.core.Workflow")
      .method("publishWorkflowVersion")
      .data({ id: id, message: message, publish: publish })
      .execute();
  }

  private displayRightSlideInWindow(id: string, tableName: string) {
    this.legacyNavigateService.rightSlideInWindow(
      {
        'tile': 'DetailsAutoScaffold',
        'parameter-values': {
          table: tableName,
          id: id
        },
        'loadOptions' : {
          cache : false
        }
      }, async (operation) => await this.handleRightSlideInWindowEvent(operation));
  }

  private async handleRightSlideInWindowEvent(operation: {data: any, envelope: any}) {
    // operation.envelope.data.eventName is 'delete' for a delete operation and 'update' for a save operation.
    // Closing the popup doesn't trigger this callback.
    if (operation.envelope.channel === (window as any).MessageBus.CHANNELS.TABLE_EVENT) {
      if (operation.envelope.data.eventName === "update") {
        const id = operation.data.id;
        const data = this.getNodeModelById(id);
        await this.saveLocation(data);
      }
      await this.synchronizeWorkflowData();
      this.updateDiagramFromModel();
    }
  }

  private async prompt(i18nKey: string) {
    return new Promise((resolve, reject) => {
      this.alertService.prompt(i18nKey, (response: string, data: string) => {
        resolve({response: response, data: data});
      });
    });
  }

  private async createLocalizedText(prefix: string, text: string) {
    const i18nKey = [prefix,
      (window as any).StringUtils.toCamelCase(this.workflowData.Workflow.Name),
      (window as any).StringUtils.toCamelCase(text),
      "Label"].join(".");

    // Push the new key to i18n client repository so it can be used right away.
    this.i18nService.cacheMessage(i18nKey, text);

    const data = {
      Locale: "en_US",
      I18nKey: i18nKey,
      Message: text
    };

    // Check to see if the key already exists or not
    const alreadyExists = (await (window as any).REST.get("LocalizedMessage", null, { I18nKey: i18nKey }))?.data?.length > 0;

    if (!alreadyExists) {
      await this.requestService.createRequest()
        .async(false)
        .controller("servicely.core.System")
        .method("create")
        .table("LocalizedMessage")
        .data(data)
        .execute();
    }

    return i18nKey;
  }

  // Modal support

  modalCssClass() {
    return this.isEdit ? '' : 'workflow-modal';
  }

  // Form support
  async getCheckoutResult(): Promise<any> {
    return await this.requestService.createRequest()
      .async(true)
      .controller("servicely.core.Workflow")
      .method("checkWorkflowIsCheckedOut")
      .data({ id: this.workflowId})
      .execute();
  }

  async layoutDiagram(event) {
    this.gojsDiagram.layoutDiagram(true);
    this.saveLayout(null);
  }

  async saveLayout(event) {
    if (await this.checkUserHasWorkflowCheckedOutAndDisplayMessage()) {
      const model = this.getModel();
      const nodeData = model.nodeDataArray;

      await this.saveLocation(nodeData);

      const linkData = model.linkDataArray;

      await this.savePoints(linkData);
    }
  }

  async saveTemporaryVersion(event) {
    await this.showDescriptionDialogAndPublish(
      'Please provide a description of the update:<br /><b>Note: this version will be set as the default for *your* current session only</pb>',
      false,
      'Done',
      event
    );
  }

  async publishVersion(event) {
    await this.showDescriptionDialogAndPublish(
      'Please provide a description of the update:<br /><b>Note: this version will be set as the default for new workflows</pb>',
      true,
      'Done',
      event
    );
  }

  async checkin(event) {
    if (await this.checkUserHasWorkflowCheckedOutAndDisplayMessage()) {
      await this.genericServerRequest('servicely.core.Workflow', 'checkInWorkflow', {id: this.workflowId}, 'Success', (response) => {
        this.backToWorkflow();
      });
    }
  }

  async checkout(event) {
    await this.genericServerRequest('servicely.core.Workflow', 'checkOutWorkflow', {id: this.workflowId}, 'Success', (response) => {
      this.initData();
    });
  }

  async genericServerRequest(controller: string, method: string, data: any, successMessage: string, callback: (response) => void) {
    await this.requestService.createRequest()
      .async(true)
      .controller(controller)
      .method(method)
      .data(data)
      .errorFunc((response) => {
        const message = response?.responseJSON?.message || response.responseText;
        this.alertService.errorMessage(message);
      })
      .successFunc((response) => {
        if (response.data?.error === true) {
          this.alertService.errorMessage(response.data?.message);
        } else {
          if (successMessage) {
            this.alertService.success(successMessage);
          }
          if (callback != null) {
            callback(response);
          }
        }
      })
      .execute();
  }

  async showDescriptionDialogAndPublish(message: string, publish: boolean, publishMessage: string, event: any) {
    if (await this.checkUserHasWorkflowCheckedOutAndDisplayMessage()) {
      this.alertService.prompt(message, async (dialogButton, commitMessage) => {
        if (dialogButton === "ok") {
          if (await this.checkUserHasWorkflowCheckedOutAndDisplayMessage()) {
            await this.saveLayout(event);
            await this.publishWorkflowVersion(this.workflowId, commitMessage, publish);
            this.alertService.success(publishMessage);
          }
        } else {
          this.alertService.infoMessage("Not performing action");
        }
      });
    }
  }

  async checkUserHasWorkflowCheckedOutAndDisplayMessage(): Promise<boolean> {
    let checkResponse: any = await this.getCheckoutResult();

    if (checkResponse.data != null) {
      checkResponse = checkResponse.data;
    }

    if (checkResponse.isWorkflowCheckedOutForUser === true) {
      return true;
    } else {
      this.isCheckedOut = false;
      this.isCheckedOutByThisUser = false;
      if (checkResponse.checkedOutForUser != null) {
        this.alertService.infoMessage(`Workflow is checked out by another user (${checkResponse.checkedOutForUser})`);
      } else {
        this.alertService.infoMessage(`Workflow is not checked out.`);
      }
    }
    return false;
  }

  private async saveLocation(nodeDatas: any) {
    const addPX = (s: number): string => {
      return `${s}px`;
    };

    const ndr = Array.isArray(nodeDatas) ? nodeDatas : [nodeDatas];

    // TODO: Should make this a special call to WorkflowController so we can batch them up.
    ndr.forEach((nodeData) => {
      if (nodeData) {
        const id = nodeData.id;
        const category = nodeData.category;
        const point = go.Point.parse(nodeData.loc);

        const data = {
          LeftPosition: addPX(point.x),
          TopPosition: addPX(point.y)
        };

        this.updateRecord(data, id, category);
      }
    });
  }

  private async savePoints(linkDatas: any) {
    const ldr = Array.isArray(linkDatas) ? linkDatas : [linkDatas];
    ldr.forEach((linkData) => {
      if (linkData) {
        const id = linkData.id;

        const points = this.pointListToPointArrayString(linkData.points);

        const data = {
          Points: points,
        };

        this.updateRecord(data, id, "WorkflowTransition");
      }
    });
  }

  private findMapKeyByValue(map: any, value: any) {
    return Object.keys(map).find(key => map[key] === value);
  }

  private pointArrayStringToPointList(p: string): go.List<go.Point> {
    let result = null;
    if (p && p.length > 0) {
      const parray: number[] = JSON.parse(p);
      const pairArray = parray.reduce(function(result, value, index, array) {
        if (index % 2 === 0) {
          result.push(array.slice(index, index + 2));
        }
        return result;
      }, []);
      result = new go.List<go.Point>();
      pairArray.forEach((p) => {
        result.push(new go.Point(p[0], p[1]));
      });
    }

    return result;
  }

  private pointListToPointArrayString(pl: go.List<go.Point>): string {
    let result = null;
    if (pl && pl.length > 0) {
      const points: number[] = [];
      pl.map((p) => {
        points.push(p.x, p.y);
      });
      result = JSON.stringify(points);
    }
    return result;
  }

  // Fake button navigation

  buttonLabel(key: string): string {
    return this.i18nService.getMessage(key);
  }

  canGoBack() {
    return this.legacyNavigateService.canGoBack();
  }

  back() {
    this.legacyNavigateService.back();
  }
}
